import { Box, Typography } from "@mui/material";
import { useEffect, useState, type ReactNode } from "react";
import { NEON_GLOW, NeonEyebrow } from "./Neon.js";
import { proxied, isLikelyImageUrl } from "../lib/imageProxy.js";
import type { PublicContent } from "../hooks/useApiQueries.js";

interface HeroVideoCarouselProps {
  videos: PublicContent[];
  intervalMs?: number;
  eyebrow?: string;
  title?: ReactNode;
  height?: { xs: number; md: number };
}

function youTubeId(url: string | null | undefined): string | null {
  if (!url) return null;
  const m = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/);
  return m?.[1] ?? null;
}

function youTubeEmbedSrc(videoId: string, muted: boolean) {
  // Background video params: muted+autoplay (required for autoplay), loop via
  // the playlist trick, no controls/branding/related videos. modestbranding
  // hides most YouTube chrome.
  const params = new URLSearchParams({
    autoplay: "1",
    mute: muted ? "1" : "0",
    controls: "0",
    rel: "0",
    showinfo: "0",
    loop: "1",
    playlist: videoId,
    modestbranding: "1",
    playsinline: "1",
    iv_load_policy: "3",
  });
  return `https://www.youtube.com/embed/${videoId}?${params}`;
}

const DEFAULT_TITLE = (
  <>
    Real machines.
    <br />
    <Box component="span" sx={{ color: "primary.main", textShadow: NEON_GLOW }}>
      Real operators.
    </Box>
    <br />
    Real dirt.
  </>
);

type Slide =
  | { kind: "youtube"; id: string; thumb: string | null; title: string }
  | { kind: "image"; thumb: string; title: string };

export function HeroVideoCarousel({
  videos,
  intervalMs = 8000,
  eyebrow = "CORIAN PARK · CURATED EARTHMOVING",
  title = DEFAULT_TITLE,
  height,
}: HeroVideoCarouselProps) {
  type YouTubeSlide = Extract<Slide, { kind: "youtube" }>;
  const playable: YouTubeSlide[] = videos
    .map((v) => {
      const id = youTubeId(v.embedUrl ?? v.sourceUrl);
      return id ? ({ kind: "youtube" as const, id, thumb: v.thumbnailUrl, title: v.title }) : null;
    })
    .filter((v): v is YouTubeSlide => v !== null)
    .slice(0, 5);

  // Fallback when none of the inputs have valid YouTube IDs (RSS sources,
  // missing embedUrls, etc) — cycle the thumbnails as still images so the
  // hero never goes blank with just overlay text on a black field.
  const imageFallback: Slide[] =
    playable.length === 0
      ? videos
          .filter((v) => isLikelyImageUrl(v.thumbnailUrl))
          .slice(0, 5)
          .map((v) => ({ kind: "image", thumb: v.thumbnailUrl as string, title: v.title }))
      : [];

  const slides: Slide[] = playable.length > 0 ? playable : imageFallback;

  const [index, setIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(max-width: 768px), (pointer: coarse)");
    setIsMobile(mq.matches);
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  useEffect(() => {
    if (slides.length <= 1) return;
    const t = setInterval(() => setIndex((i) => (i + 1) % slides.length), intervalMs);
    return () => clearInterval(t);
  }, [slides.length, intervalMs]);

  const renderIframes = !isMobile && playable.length > 0;

  return (
    <Box
      sx={{
        // Full-bleed escape: break out of the parent Container (max-width lg)
        // to viewport edges. left/right + negative margins is the standard
        // technique that works without restructuring Layout.
        position: "relative",
        left: "50%",
        right: "50%",
        ml: "-50vw",
        mr: "-50vw",
        width: "100vw",
        mt: -4,
        mb: { xs: 5, md: 8 },
        height: height ?? { xs: 480, md: 640 },
        overflow: "hidden",
        borderBottom: "1px solid",
        borderColor: "divider",
        bgcolor: "common.black",
      }}
    >
      {/* Stacked videos — render all then fade between them. Cheaper than
          remounting on every cycle (avoids YouTube cold-start each time). */}
      {renderIframes &&
        playable.map((v, i) => (
          <Box
            key={v.id}
            sx={{
              position: "absolute",
              inset: 0,
              opacity: i === index ? 1 : 0,
              transition: "opacity 1200ms ease-in-out",
              pointerEvents: "none",
            }}
          >
            <Box
              component="iframe"
              src={youTubeEmbedSrc(v.id, true)}
              title={v.title}
              allow="autoplay; encrypted-media; picture-in-picture"
              loading="lazy"
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                width: "177.78vh",
                minWidth: "100%",
                height: "56.25vw",
                minHeight: "100%",
                transform: "translate(-50%, -50%)",
                border: 0,
                pointerEvents: "none",
              }}
            />
          </Box>
        ))}

      {/* Image fallback — used on mobile, OR on desktop when no playable
          YouTube videos exist (e.g. RSS-only sources). Cross-fades like the
          iframe path. */}
      {!renderIframes &&
        slides.map((s, i) =>
          isLikelyImageUrl(s.thumb) ? (
            <Box
              key={s.kind === "youtube" ? s.id : s.thumb}
              component="img"
              src={proxied(s.thumb!)}
              alt=""
              loading={i === 0 ? "eager" : "lazy"}
              sx={{
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
                objectFit: "cover",
                opacity: i === index ? 1 : 0,
                transition: "opacity 1200ms ease-in-out",
              }}
            />
          ) : null,
        )}

      {/* Last-resort: no slides at all → use a saturated brand gradient so
          the hero still feels intentional rather than empty. */}
      {slides.length === 0 && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background:
              "radial-gradient(ellipse at 30% 30%, rgba(255,176,0,0.22) 0%, transparent 60%), radial-gradient(ellipse at 70% 80%, rgba(255,136,0,0.18) 0%, transparent 60%), linear-gradient(180deg, #1a1a1a 0%, #0d0d0d 100%)",
          }}
        />
      )}

      {/* Wash + accent gradient over the video */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          background:
            "linear-gradient(180deg, rgba(13,13,13,0.55) 0%, rgba(13,13,13,0.85) 70%, rgba(13,13,13,0.96) 100%), radial-gradient(ellipse at top right, rgba(255,176,0,0.18) 0%, transparent 60%)",
          pointerEvents: "none",
        }}
      />

      {/* Bottom neon stripe */}
      <Box
        sx={{
          position: "absolute",
          left: 0,
          bottom: 0,
          width: "100%",
          height: 4,
          background: "linear-gradient(90deg, #ffb000 0%, #ff8800 50%, transparent 100%)",
          boxShadow: NEON_GLOW,
          pointerEvents: "none",
        }}
      />

      {/* Overlay copy */}
      <Box
        sx={{
          position: "relative",
          maxWidth: 1200,
          mx: "auto",
          px: { xs: 2, sm: 3, md: 4 },
          pt: { xs: 6, md: 12 },
          pb: { xs: 5, md: 10 },
          color: "common.white",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-end",
        }}
      >
        <NeonEyebrow glow sx={{ fontSize: 14, letterSpacing: 4 }}>
          {eyebrow}
        </NeonEyebrow>
        <Typography
          variant="h1"
          sx={{
            fontSize: { xs: 44, sm: 64, md: 88 },
            fontWeight: 900,
            lineHeight: 0.95,
            letterSpacing: "-0.02em",
            mt: 2,
            maxWidth: 900,
            textShadow: "0 4px 30px rgba(0,0,0,0.6)",
          }}
        >
          {title}
        </Typography>
      </Box>

      {/* Slide indicators */}
      {slides.length > 1 && (
        <Box
          sx={{
            position: "absolute",
            bottom: 16,
            right: 16,
            display: "flex",
            gap: 0.75,
            zIndex: 2,
          }}
        >
          {slides.map((_, i) => (
            <Box
              key={i}
              onClick={() => setIndex(i)}
              sx={{
                width: i === index ? 24 : 8,
                height: 8,
                borderRadius: 4,
                bgcolor: i === index ? "primary.main" : "rgba(255,255,255,0.4)",
                cursor: "pointer",
                transition: "all 200ms ease",
                boxShadow: i === index ? NEON_GLOW : "none",
              }}
            />
          ))}
        </Box>
      )}
    </Box>
  );
}
